import React, { useCallback } from 'react'
import useFlightPurchaseDetails from 'hooks/Flights/useFlightPurchaseDetails'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import BodyText from 'LuxKit/Typography/BodyText'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'

interface Props {
  order: App.Order
  isPastOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
}

export default function LesaStandaloneFlightPurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap } = props

  const dispatch = useAppDispatch()

  const {
    fetching,
    flightsName,
    departingDate,
    arrivalDate,
    noOfAdults,
    noOfChildren,
    noOfInfants,
    flightTypeLabel,
    pnrIds,
  } = useFlightPurchaseDetails(order)

  const isListFlightsOrdersAction = !!componentsMap.get('list-flight-orders')
  const itemId = order.flightItems[0].id

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: isListFlightsOrdersAction ? 'selectFlightOrder' : 'selectOrder', payload: { id_orders: order.id, itemId } }))
  }, [dispatch, isListFlightsOrdersAction, itemId, order.id])

  if (fetching) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['changeDates', 'changeRoom', 'listInclusions', 'showFinePrint', 'resendConfirmationEmail']))

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('list-flight-orders') || !!componentsMap?.get('selectFlightOrder') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LinePlaneDepartureIcon size="XS" />}>Flights</BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">Upcoming</StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">Past</StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {flightsName}
              </LesaPurchaseSummaryTitle>
              <BodyTextBlock variant="small" colour="neutral-two">
                {moment(departingDate).format(DMY_CASUAL_SHORT_FORMAT)}
                {' '}-{' '}
                {moment(arrivalDate).format(DMY_CASUAL_SHORT_FORMAT)}
              </BodyTextBlock>
              <LesaPurchaseSummarySeparator>
                <BodyText as="span" variant="small" colour="neutral-three">{formatOccupantsShort({
                  adults: noOfAdults,
                  children: noOfChildren,
                  infants: noOfInfants,
                })}
                </BodyText>
                <BodyText as="span" variant="small" colour="neutral-three">{flightTypeLabel}</BodyText>
              </LesaPurchaseSummarySeparator>
              {pnrIds?.length && <BodyTextBlock variant="small" colour="neutral-two"><strong>Booking ID:</strong> {pnrIds.join(', ')}</BodyTextBlock>}{' '}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                Select
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}
